import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Calendar, ChevronDown, ArrowDown, ArrowUp, DollarSign, Users, Clock, ChevronRight } from 'lucide-react';
import './DashboardVentas.css';

// Datos simulados de ventas
const generarDatosVentas = () => {
  const fechaActual = new Date();
  const ultimosDias = 30;
  const datos = [];

  for (let i = ultimosDias - 1; i >= 0; i--) {
    const fecha = new Date();
    fecha.setDate(fechaActual.getDate() - i);

    // Simular variaciones realistas de ventas
    const factorDiaSemana = [0.7, 0.8, 0.9, 1.0, 1.1, 1.5, 1.3][fecha.getDay()]; // Menos ventas Lun-Mié, más Vie-Dom
    const baseVentas = Math.floor(10000 + Math.random() * 5000);
    const ventasDia = Math.floor(baseVentas * factorDiaSemana);

    // Variación en número de transacciones
    const transacciones = Math.floor(50 + Math.random() * 30 * factorDiaSemana);

    // Ticket promedio (ventas/transacciones)
    const ticketPromedio = Math.floor(ventasDia / transacciones);

    // Productos por categoría
    const categorias = {
      'Bebidas': Math.floor(ventasDia * (0.2 + Math.random() * 0.1)),
      'Entradas': Math.floor(ventasDia * (0.15 + Math.random() * 0.08)),
      'Platos Fuertes': Math.floor(ventasDia * (0.35 + Math.random() * 0.15)),
      'Postres': Math.floor(ventasDia * (0.1 + Math.random() * 0.05)),
      'Extras': Math.floor(ventasDia * (0.05 + Math.random() * 0.03))
    };

    // Métodos de pago
    const pagoEfectivo = Math.floor(ventasDia * (0.4 + Math.random() * 0.2));
    const pagoTarjeta = ventasDia - pagoEfectivo;

    // Tipos de pedido
    const tiposPedido = {
      'Mesa': Math.floor(transacciones * (0.6 + Math.random() * 0.2)),
      'Llevar': Math.floor(transacciones * (0.2 + Math.random() * 0.1)),
      'Domicilio': Math.floor(transacciones * (0.1 + Math.random() * 0.1))
    };

    datos.push({
      fecha: fecha.toISOString().split('T')[0],
      diaSemana: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'][fecha.getDay()],
      ventas: ventasDia,
      transacciones,
      ticketPromedio,
      categorias,
      pagoEfectivo,
      pagoTarjeta,
      tiposPedido
    });
  }

  return datos;
};

// Generar datos de productos más vendidos
const generarProductosVendidos = () => {
  const productos = [
    { nombre: 'Hamburguesa Especial', categoria: 'Platos Fuertes', precio: 120 },
    { nombre: 'Refresco', categoria: 'Bebidas', precio: 25 },
    { nombre: 'Cerveza', categoria: 'Bebidas', precio: 40 },
    { nombre: 'Pasta Alfredo', categoria: 'Platos Fuertes', precio: 110 },
    { nombre: 'Ensalada César', categoria: 'Entradas', precio: 80 },
    { nombre: 'Pizza Hawaiana', categoria: 'Platos Fuertes', precio: 150 },
    { nombre: 'Nachos con Queso', categoria: 'Entradas', precio: 95 },
    { nombre: 'Pastel de Chocolate', categoria: 'Postres', precio: 65 },
    { nombre: 'Agua Mineral', categoria: 'Bebidas', precio: 20 },
    { nombre: 'Helado', categoria: 'Postres', precio: 45 },
    { nombre: 'Papas Fritas', categoria: 'Extras', precio: 35 },
    { nombre: 'Alitas BBQ', categoria: 'Entradas', precio: 100 },
    { nombre: 'Filete de Pescado', categoria: 'Platos Fuertes', precio: 160 },
    { nombre: 'Té Helado', categoria: 'Bebidas', precio: 30 },
    { nombre: 'Flan', categoria: 'Postres', precio: 50 }
  ];

  return productos.map(producto => ({
    ...producto,
    cantidad: Math.floor(20 + Math.random() * 300),
    total: 0 // Se calculará después
  })).map(producto => ({
    ...producto,
    total: producto.cantidad * producto.precio
  })).sort((a, b) => b.total - a.total); // Ordenar por total de ventas
};

// Componente para una card de métrica
const MetricaCard = ({ icon, titulo, valor, comparacion, porcentaje, tendencia }) => {
  return (
    <div className="metrica-card">
      <div className="metrica-card-inner">
        <div className="metrica-header">
          <div className="metrica-titulo">{titulo}</div>
          <div className={`metrica-icono ${tendencia === 'up' ? 'positivo' : 'negativo'}`}>
            {icon}
          </div>
        </div>
        <div className="metrica-valor">{valor}</div>
        <div className={`metrica-comparacion ${tendencia === 'up' ? 'positivo' : 'negativo'}`}>
          {tendencia === 'up' ? (
            <ArrowUp size={16} className="mr-1" />
          ) : (
            <ArrowDown size={16} className="mr-1" />
          )}
          <span>{porcentaje}%</span>
          <span className="periodo">vs {comparacion}</span>
        </div>
      </div>
    </div>
  );
};

// Colores personalizados para los gráficos - una paleta más agradable
const COLORS = ['#4361ee', '#3a0ca3', '#7209b7', '#f72585', '#4cc9f0', '#4895ef'];

// Componente principal del reporte
const DashboardVentas = () => {
  const [datosVentas, setDatosVentas] = useState([]);
  const [productosVendidos, setProductosVendidos] = useState([]);
  const [periodoSeleccionado, setPeriodoSeleccionado] = useState('mes');
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    // Simular carga de datos
    setCargando(true);
    setTimeout(() => {
      setDatosVentas(generarDatosVentas());
      setProductosVendidos(generarProductosVendidos());
      setCargando(false);
    }, 1000);
  }, []);

  // Calcular métricas generales
  const calcularMetricas = () => {
    if (datosVentas.length === 0) return null;

    const ventasUltimos7Dias = datosVentas.slice(-7).reduce((sum, dia) => sum + dia.ventas, 0);
    const ventasAnteriores7Dias = datosVentas.slice(-14, -7).reduce((sum, dia) => sum + dia.ventas, 0);
    const cambioVentas = ((ventasUltimos7Dias - ventasAnteriores7Dias) / ventasAnteriores7Dias * 100).toFixed(1);

    const transaccionesUltimos7Dias = datosVentas.slice(-7).reduce((sum, dia) => sum + dia.transacciones, 0);
    const transaccionesAnteriores7Dias = datosVentas.slice(-14, -7).reduce((sum, dia) => sum + dia.transacciones, 0);
    const cambioTransacciones = ((transaccionesUltimos7Dias - transaccionesAnteriores7Dias) / transaccionesAnteriores7Dias * 100).toFixed(1);

    const ticketPromedioUltimos7Dias = ventasUltimos7Dias / transaccionesUltimos7Dias;
    const ticketPromedioAnteriores7Dias = ventasAnteriores7Dias / transaccionesAnteriores7Dias;
    const cambioTicket = ((ticketPromedioUltimos7Dias - ticketPromedioAnteriores7Dias) / ticketPromedioAnteriores7Dias * 100).toFixed(1);

    // Calcular horas pico (simuladas)
    const horasPico = Math.floor(12 + Math.random() * 8);
    const cambioHorasPico = (Math.random() * 10 - 5).toFixed(1);

    return {
      ventasUltimos7Dias,
      cambioVentas,
      transaccionesUltimos7Dias,
      cambioTransacciones,
      ticketPromedioUltimos7Dias,
      cambioTicket,
      horasPico,
      cambioHorasPico
    };
  };

  const metricas = calcularMetricas();

  // Preparar datos para gráficos
  const datosGraficoVentas = datosVentas.map(dia => ({
    fecha: `${dia.diaSemana} ${dia.fecha.slice(-2)}`,
    ventas: dia.ventas
  }));

  const datosGraficoTransacciones = datosVentas.map(dia => ({
    fecha: `${dia.diaSemana} ${dia.fecha.slice(-2)}`,
    transacciones: dia.transacciones
  }));

  // Datos para gráfico de categorías
  const datosCategorias = Object.entries(
    datosVentas.reduce((acc, dia) => {
      Object.entries(dia.categorias).forEach(([categoria, monto]) => {
        acc[categoria] = (acc[categoria] || 0) + monto;
      });
      return acc;
    }, {})
  ).map(([name, value]) => ({ name, value }));

  // Datos para tipos de pedido
  const datosTiposPedido = Object.entries(
    datosVentas.reduce((acc, dia) => {
      Object.entries(dia.tiposPedido).forEach(([tipo, cantidad]) => {
        acc[tipo] = (acc[tipo] || 0) + cantidad;
      });
      return acc;
    }, {})
  ).map(([name, value]) => ({ name, value }));

  // Calcular totales de métodos de pago
  const calcularTotalesMetodosPago = () => {
    if (datosVentas.length === 0) return { efectivo: 0, tarjeta: 0, total: 0 };

    const efectivo = datosVentas.reduce((sum, dia) => sum + dia.pagoEfectivo, 0);
    const tarjeta = datosVentas.reduce((sum, dia) => sum + dia.pagoTarjeta, 0);
    const total = efectivo + tarjeta;

    return {
      efectivo,
      tarjeta,
      porcentajeEfectivo: ((efectivo / total) * 100).toFixed(0),
      porcentajeTarjeta: ((tarjeta / total) * 100).toFixed(0)
    };
  };

  const metodosPago = calcularTotalesMetodosPago();

  if (cargando) {
    return (
      <div className="pantalla-carga">
        <div className="spinner"></div>
        <span>Cargando datos...</span>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        {/* Encabezado */}
        <div className="dashboard-header">
          <div className="dashboard-titulo">
            <h1>Dashboard de Ventas</h1>
            <p>Resumen de métricas y análisis de ventas</p>
          </div>
          <div className="dashboard-controles">
            <div className="selector-fecha">
              <Calendar size={18} className="mr-2" />
              <span>Últimos 30 días</span>
              <ChevronDown size={16} className="ml-2" />
            </div>
            <div className="selector-periodo">
              <button
                className={periodoSeleccionado === 'semana' ? 'activo' : ''}
                onClick={() => setPeriodoSeleccionado('semana')}
              >
                Semana
              </button>
              <button
                className={periodoSeleccionado === 'mes' ? 'activo' : ''}
                onClick={() => setPeriodoSeleccionado('mes')}
              >
                Mes
              </button>
              <button
                className={periodoSeleccionado === 'anual' ? 'activo' : ''}
                onClick={() => setPeriodoSeleccionado('anual')}
              >
                Año
              </button>
            </div>
          </div>
        </div>

        {/* Tarjetas de métricas */}
        {metricas && (
          <div className="metricas-grid">
            <MetricaCard
              icon={<DollarSign size={20} />}
              titulo="Ventas Totales"
              valor={`$${metricas.ventasUltimos7Dias.toLocaleString()}`}
              comparacion="semana anterior"
              porcentaje={metricas.cambioVentas}
              tendencia={Number(metricas.cambioVentas) >= 0 ? 'up' : 'down'}
            />
            <MetricaCard
              icon={<Users size={20} />}
              titulo="Transacciones"
              valor={metricas.transaccionesUltimos7Dias.toLocaleString()}
              comparacion="semana anterior"
              porcentaje={metricas.cambioTransacciones}
              tendencia={Number(metricas.cambioTransacciones) >= 0 ? 'up' : 'down'}
            />
            <MetricaCard
              icon={<DollarSign size={20} />}
              titulo="Ticket Promedio"
              valor={`$${Math.round(metricas.ticketPromedioUltimos7Dias).toLocaleString()}`}
              comparacion="semana anterior"
              porcentaje={metricas.cambioTicket}
              tendencia={Number(metricas.cambioTicket) >= 0 ? 'up' : 'down'}
            />
            <MetricaCard
              icon={<Clock size={20} />}
              titulo="Hora Pico"
              valor={`${metricas.horasPico}:00`}
              comparacion="semana anterior"
              porcentaje={metricas.cambioHorasPico}
              tendencia={Number(metricas.cambioHorasPico) >= 0 ? 'up' : 'down'}
            />
          </div>
        )}

        {/* Gráficos principales */}
        <div className="graficos-grid">
          <div className="grafico-card">
            <h2 className="grafico-titulo">Tendencia de Ventas</h2>
            <div className="grafico-contenido">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={datosGraficoVentas} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="fecha" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      border: 'none'
                    }}
                    formatter={(value) => [`$${value.toLocaleString()}`, 'Ventas']}
                  />
                  <Line
                    type="monotone"
                    dataKey="ventas"
                    stroke="#4361ee"
                    strokeWidth={3}
                    dot={{ fill: '#4361ee', r: 4 }}
                    activeDot={{ r: 6, fill: '#3a0ca3' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grafico-card">
            <h2 className="grafico-titulo">Transacciones Diarias</h2>
            <div className="grafico-contenido">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={datosGraficoTransacciones} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="fecha" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      border: 'none'
                    }}
                    formatter={(value) => [`${value.toLocaleString()}`, 'Transacciones']}
                  />
                  <Bar
                    dataKey="transacciones"
                    fill="#7209b7"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Gráficos secundarios */}
        <div className="graficos-secundarios-grid">
          <div className="grafico-card">
            <h2 className="grafico-titulo">Ventas por Categoría</h2>
            <div className="grafico-pie-contenido">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={datosCategorias}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {datosCategorias.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      border: 'none'
                    }}
                    formatter={(value) => [`$${value.toLocaleString()}`, 'Ventas']}
                  />
                  <Legend layout="vertical" verticalAlign="middle" align="right" />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grafico-card">
            <h2 className="grafico-titulo">Tipos de Pedido</h2>
            <div className="grafico-pie-contenido">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={datosTiposPedido}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {datosTiposPedido.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      border: 'none'
                    }}
                    formatter={(value) => [`${value.toLocaleString()}`, 'Pedidos']}
                  />
                  <Legend layout="vertical" verticalAlign="middle" align="right" />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Tabla de productos más vendidos */}
        <div className="tabla-card">
          <div className="tabla-header">
            <h2 className="tabla-titulo">Productos Más Vendidos</h2>
            <div className="tabla-link">
              <span>Ver todos</span>
              <ChevronRight size={16} className="ml-1" />
            </div>
          </div>

          <div className="tabla-scroll">
            <table className="tabla-productos">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Categoría</th>
                  <th>Precio</th>
                  <th>Cantidad</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {productosVendidos.slice(0, 10).map((producto, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'par' : 'impar'}>
                    <td className="producto-nombre">{producto.nombre}</td>
                    <td className="producto-categoria">{producto.categoria}</td>
                    <td className="producto-precio">${producto.precio.toLocaleString()}</td>
                    <td className="producto-cantidad">{producto.cantidad.toLocaleString()}</td>
                    <td className="producto-total">${producto.total.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Estadísticas adicionales */}
        <div className="estadisticas-grid">
          <div className="estadistica-card">
            <h2 className="estadistica-titulo">Métodos de Pago</h2>
            <div className="metodos-pago-contenido">
              <div className="metodo-pago-item">
                <div className="metodo-pago-header">
                  <span className="metodo-nombre">Efectivo</span>
                  <span className="metodo-porcentaje">{metodosPago.porcentajeEfectivo}%</span>
                </div>
                <div className="metodo-pago-barra-bg">
                  <div
                    className="metodo-pago-barra efectivo"
                    style={{ width: `${metodosPago.porcentajeEfectivo}%` }}
                  ></div>
                </div>
              </div>
              <div className="metodo-pago-item">
                <div className="metodo-pago-header">
                  <span className="metodo-nombre">Tarjeta</span>
                  <span className="metodo-porcentaje">{metodosPago.porcentajeTarjeta}%</span>
                </div>
                <div className="metodo-pago-barra-bg">
                  <div
                    className="metodo-pago-barra tarjeta"
                    style={{ width: `${metodosPago.porcentajeTarjeta}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          <div className="estadistica-card">
            <h2 className="estadistica-titulo">Rendimiento por Día</h2>
            <div className="rendimiento-dias-contenido">
              {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((dia, index) => {
                const porcentaje = [55, 60, 65, 75, 90, 100, 85][index];
                return (
                  <div key={dia} className="rendimiento-dia-item">
                    <div className="rendimiento-dia-header">
                      <span className="dia-nombre">{dia}</span>
                      <span className="dia-porcentaje">{porcentaje}%</span>
                    </div>
                    <div className="rendimiento-barra-bg">
                      <div
                        className={`rendimiento-barra ${index >= 4 ? 'fin-semana' : 'semana'}`}
                        style={{ width: `${porcentaje}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="estadistica-card">
            <h2 className="estadistica-titulo">Indicadores de Desempeño</h2>
            <div className="indicadores-contenido">
              <div className="indicador-item">
                <div className="indicador-info">
                  <p className="indicador-nombre">Eficiencia de meseros</p>
                  <p className="indicador-valor">93%</p>
                </div>
                <div className="indicador-icono positivo">
                  <ArrowUp size={20} />
                </div>
              </div>
              <div className="indicador-item">
                <div className="indicador-info">
                  <p className="indicador-nombre">Tiempo promedio de atención</p>
                  <p className="indicador-valor">12 min</p>
                </div>
                <div className="indicador-icono positivo">
                  <ArrowUp size={20} />
                </div>
              </div>
              <div className="indicador-item">
                <div className="indicador-info">
                  <p className="indicador-nombre">Satisfacción de clientes</p>
                  <p className="indicador-valor">89%</p>
                </div>
                <div className="indicador-icono negativo">
                  <ArrowDown size={20} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardVentas;